import { legacyLocalLabel } from '../../../../utilities/legacyDatePresentation';
import { useCallback } from "react";
import React, { useEffect, useState } from 'react';
import { FileText, Eye, Trash2, Edit2 } from 'lucide-react';
import { GetAllPlanReq, PlanDto } from '../../../../interfaces';
import { useAuth } from '../../../../firebase/AuthProvider';
import { getAllPlanAsync } from '../../../../services/PlanService/PlanService';
import CreatePlanModal from './modals/CreatePlanModal';
import EditPlanModal from './modals/EditPlanModal';
import { DeletePlanAsync } from '../../../../services/PlanService/PlanService';
import { Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { Button } from 'react-bootstrap';
import { showConfirmationDialog } from '../../../modal/ConfimationModal/Confimation';
import Pagination from '../../../ui/Pagination/Pagination';
import { PaginationDto } from '../../../../interfaces/Employee/Employee';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../../Data/Routes/Routes';
import { useAbortManager } from '../../../../hooks/cancelletionManagerHook/useAbortManager';
import { getDisplayErrorMessage } from '../../../../utilities/ErrorMessages/getErrorMessage';

const Plans = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    const [plans, setPlans] = useState<PlanDto[]>([]);
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [editingPlan, setEditingPlan] = useState<PlanDto | null>(null);
    const [reload, setReload] = useState<boolean>(true);
    const [isLoadingPlans, setIsLoadingPlans] = useState<boolean>(false);
    const [planCount, setPlanCount] = useState<number>(0);
    const [pagination, setPagination] = useState<PaginationDto>({ pageNumber: 1, pageSize: 10 });
    const [sortKey, setSortKey] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    // map for the cancelletion
    const cancelletionManager = useAbortManager();

    const GetAllPlans = useCallback(async () => {
        // Cancel ONLY previous GetAllPlans request
        cancelletionManager.abort("getAllPlans");
        setIsLoadingPlans(true);

        try {
            const controller = cancelletionManager.create("getAllPlans");
            const token = await currentUser?.getIdToken();
            if (!token) return;

            const payload: GetAllPlanReq = {
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
                sortBy: sortKey,
                sortOrder: sortDirection,
                onlyActive: false,
                includeFeatures: false
            };

            const planRes = await getAllPlanAsync(token, payload, controller.signal);

            if (planRes.success) {
                setPlans(planRes.data.items);
                setPlanCount(planRes.data.totalCount);
            } else {
                toast.error(planRes.message);
            }

        } catch (error: any) {
            if (error.name === 'AbortError' || error.name === 'CanceledError') return;
            const msg =  getDisplayErrorMessage(error, "Error to get the data");
            toast.error(msg);
        } finally {
            cancelletionManager.abort("getAllPlans");
            setIsLoadingPlans(false);
        }
    }, [currentUser, pagination, sortKey, sortDirection, cancelletionManager]);

useEffect(() => {
        GetAllPlans();

        return () => {
            cancelletionManager.abortAll();
        };
    }, [reload, pagination, sortKey, sortDirection, GetAllPlans, cancelletionManager]);

    const handleOpenCreate = () => setShowCreateModal(true);
    const handleCloseCreate = () => setShowCreateModal(false);

    const handleEdit = (plan: PlanDto) => {
        setEditingPlan(plan);
    };

    const handleCloseEdit = () => setEditingPlan(null);

    const handleDelete = async (planId: string) => {
        const isConfirm = await showConfirmationDialog("Are you sure?", "Do you want to delete this?");
        if (!isConfirm) {
            return;
        }

        const controller = cancelletionManager.create(`delete-${planId}`);
        setDeletingId(planId);

        try {
            const token = await currentUser?.getIdToken();
            if (!token) return;

            const res = await DeletePlanAsync(token, planId, controller.signal);
            if (res.success) {
                toast.success(res.message);
                setReload(!reload);
            } else {
                toast.error(res.message);
            }
        } catch (error: any) {
            if (error.name === 'AbortError') return;
            toast.error(
                error instanceof AxiosError
                    ? error.response?.data.message
                    : error.message || "Error to delete the data"
            );
        } finally {
            cancelletionManager.remove(`delete-${planId}`);
            setDeletingId(null);
        }
    };



    const onPageChange = (pageNum: number) => {
        setPagination((prev) => ({
            ...prev,
            pageNumber: pageNum
        }))
    }

    const resetPageSize = (pageNumber: number, pageSize: number) => {
        setPagination({
            pageNumber: pageNumber,
            pageSize: pageSize
        })
    }

    const handleSort = (key: string) => {
        if (sortKey === key) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDirection('asc');
        }
        // reset to first page when sorting changes
        setPagination(prev => ({ ...prev, pageNumber: 1 }));
    }

    const getSortIcon = (key: string) => {
        if (sortKey !== key) return <span className="text-gray-300 ml-1">⇅</span>;
        return <span className="text-[#7E57C2] ml-1">{sortDirection === 'asc' ? '▲' : '▼'}</span>;
    }

    return (
        <div className='common_card'>
            <div className="w-full flex flex-col flex-1 my-2">
                <div className="flex justify-between items-center px-5 py-3">
                    <h2 className="text-lg font-semibold">Plans</h2>
                    <Button
                        onClick={handleOpenCreate}
                        className="px-3 py-1 text-white rounded "
                    >
                        + Add Plan
                    </Button>
                </div>
                <div className="w-full rounded-lg border border-gray-200 bg-white flex-1 flex flex-col">
                    <div className="w-full overflow-x-auto flex-1">
                        <table className="w-full text-left border-collapse min-w-[900px]">
                            <thead className="bg-gray-50/80 border-b border-gray-200 sticky top-0 z-10">
                                <tr>
                                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase cursor-pointer" onClick={() => handleSort('name')}>Name {getSortIcon('name')}</th>
                                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase cursor-pointer" onClick={() => handleSort('billing_interval')}>Billing Interval {getSortIcon('billing_interval')}</th>
                                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase cursor-pointer" onClick={() => handleSort('is_active')}>Active {getSortIcon('is_active')}</th>
                                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase cursor-pointer" onClick={() => handleSort('base_price_cents')}>Base Price {getSortIcon('base_price_cents')}</th>
                                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase cursor-pointer" onClick={() => handleSort('seat_price_cents')}>Seat Price {getSortIcon('seat_price_cents')}</th>
                                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase cursor-pointer" onClick={() => handleSort('create_date')}>Created {getSortIcon('create_date')}</th>
                                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase cursor-pointer" onClick={() => handleSort('update_date')}>Updated {getSortIcon('update_date')}</th>
                                    <th className="px-5 py-3.5 w-12"></th>
                                </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-100 bg-white">
                                {plans.length === 0 ? (
                                    isLoadingPlans ? (
                                        <tr>
                                            <td colSpan={8} className="h-64 text-center">
                                                <div className="flex items-center justify-center h-full">
                                                    <Spinner animation="border" size="sm" className="mr-2" />
                                                    <span className="text-gray-600">Loading...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        <tr>
                                            <td colSpan={8} className="h-64 text-center">
                                                <div className="flex flex-col items-center justify-center h-full">
                                                    <FileText className="w-12 h-12 text-gray-300 mb-3" />
                                                    <p className="text-gray-500 font-medium">No plans found</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                ) : (
                                    plans.map(plan => (
                                        <tr key={plan.id} className="hover:bg-gray-50/80">
                                            <td className="px-5 py-3.5">{plan.name}</td>
                                            <td className="px-5 py-3.5">{plan.billingInterval}</td>
                                            <td className="px-5 py-3.5">
                                                {plan.isActive ? 'Active' : 'Inactive'}
                                            </td>
                                            <td className="px-5 py-3.5">{plan.basePriceCents}</td>
                                            <td className="px-5 py-3.5">{plan.seatPriceCents}</td>
                                            <td className="px-5 py-3.5">
                                                {legacyLocalLabel(plan.createDate, "MM/dd/yyyy")}
                                            </td>
                                            <td className="px-5 py-3.5">
                                                {legacyLocalLabel(plan.updateDate, "MM/dd/yyyy")}
                                            </td>
                                            <td className="px-5 py-3.5 text-right">
                                                <div className="flex items-center justify-end space-x-3">
                                                    <button type="button" title="View" onClick={() => navigate(ROUTES.PLAN_FEATURES(plan.id))} className="text-gray-500 hover:text-gray-700">
                                                        <Eye className="w-4 h-4" />
                                                    </button>

                                                    <button type="button" title="Edit" onClick={() => handleEdit(plan)} className="text-gray-500 hover:text-gray-700">
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>

                                                    <button type="button" title="Delete" onClick={() => handleDelete(plan.id)} className="text-gray-500 hover:text-gray-700 flex items-center">
                                                        {deletingId === plan.id ? (
                                                            <Spinner animation="border" size="sm" />
                                                        ) : (
                                                            !plan.isDelete && <Trash2 className="w-4 h-4" />
                                                        )}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>


                    </div>
                </div>
            </div>
            {(planCount && planCount > 0) ? (
                <Pagination
                    currentPage={pagination.pageNumber}
                    totalPages={Math.ceil(planCount / pagination.pageSize)}
                    onPageChange={onPageChange}
                    pageSize={pagination.pageSize}
                    resetPageSize={resetPageSize}
                />
            ) : <></>}
            {
                showCreateModal &&
                (
                    <CreatePlanModal
                        isOpen={showCreateModal}
                        onClose={handleCloseCreate}
                        onSuccess={() => setReload(!reload)}
                    />
                )
            }
            {
                editingPlan && (
                    <EditPlanModal
                        isOpen={Boolean(editingPlan)}
                        onClose={handleCloseEdit}
                        onSuccess={() => setReload(!reload)}
                        plan={editingPlan}
                    />
                )
            }
        </div>
    );
};

export default Plans;